import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { FC, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  IAdministrativeModeDrawerProps,
  IInjectedDrawerProps,
  isAdministrativeModeAtom,
  Log,
  PASSWORD_GATE_INPUT_DATA_ATTRS,
  priceModeAtom,
  SuperAdminPriceMode,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, FormFooter } from "../drawer";
import { AdministrativeModeWrapper } from "./parts/administrative-mode.styled";

const ADMIN_PASSWORD = "111";

interface IAdministrativeModeForm {
  administrativeModePassword: string;
  priceMode: SuperAdminPriceMode;
}

export const AdministrativeModeDrawer: FC<
  IAdministrativeModeDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const setIsAdministrativeMode = useSetRecoilState(isAdministrativeModeAtom);
  const setPriceMode = useSetRecoilState(priceModeAtom);
  const currentPriceMode = useRecoilValue(priceModeAtom);

  const { control, handleSubmit, watch } = useForm<IAdministrativeModeForm>({
    defaultValues: {
      administrativeModePassword: "",
      priceMode: SuperAdminPriceMode.Supplier,
    },
  });

  const administrativeModePassword = watch("administrativeModePassword");

  const getModeLabel = (mode: SuperAdminPriceMode): string => {
    if (mode === SuperAdminPriceMode.Client) {
      return t("general.CLIENT_PRICES");
    }
    if (mode === SuperAdminPriceMode.Supplier) {
      return t("general.SUPPLIER_PRICES");
    }
    if (mode === SuperAdminPriceMode.Both) {
      return t("general.CLIENT_AND_SUPPLIER_PRICES");
    }
    return t("general.PRICE_MODE");
  };

  const getModeClassName = (mode: SuperAdminPriceMode): string => {
    if (mode === SuperAdminPriceMode.Client) {
      return "mode-client";
    }
    if (mode === SuperAdminPriceMode.Supplier) {
      return "mode-supplier";
    }
    if (mode === SuperAdminPriceMode.Both) {
      return "mode-both";
    }
    return "";
  };

  const currentModeLabel = useMemo(() => {
    if (currentPriceMode === SuperAdminPriceMode.Client) {
      return t("general.CLIENT_PRICES");
    }
    if (currentPriceMode === SuperAdminPriceMode.Supplier) {
      return t("general.SUPPLIER_PRICE_RATES");
    }
    if (currentPriceMode === SuperAdminPriceMode.Both) {
      return t("general.CLIENT_AND_SUPPLIER_PRICES");
    }
    return t("general.CLIENT_PRICES");
  }, [currentPriceMode, t]);

  const currentModeClassName = useMemo(() => {
    if (currentPriceMode === SuperAdminPriceMode.NotSet) {
      return "mode-client";
    }
    return getModeClassName(currentPriceMode);
  }, [currentPriceMode]);

  const onSubmit = (data: IAdministrativeModeForm): void => {
    if (data.administrativeModePassword !== ADMIN_PASSWORD) {
      Log.warnAndToast(t("ui.INCORRECT_PASSWORD"));
      return;
    }

    setIsAdministrativeMode(true);
    setPriceMode(data.priceMode);

    const modeLabel = getModeLabel(data.priceMode);

    Log.infoAndToast(t("ui.MODE_SET_TO", { mode: modeLabel }));
    onCloseDrawer();
  };

  const handleCancel = (): void => {
    onCloseDrawer();
  };

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("ui.CHOOSE_SUPER_ADMIN_MODE")}
        onCloseDrawer={onCloseDrawer}
        Footer={
          <FormFooter
            cancelLabel={t("general.CANCEL")}
            onCancelClick={handleCancel}
            onSaveClick={handleSubmit(onSubmit)}
            isSaveDisabled={!administrativeModePassword}
          />
        }
      >
        <AdministrativeModeWrapper>
          <div className="login-section">
            <h2 className="login-title">
              {t("ui.LOGIN_TO_ADMINISTRATIVE_STATE")}
            </h2>
            <div className="password-field-wrapper">
              <label
                htmlFor="administrative-mode-super-admin-code-input"
                className="password-label"
              >
                {t("ui.PASSWORD")}*
              </label>
              <Controller
                control={control}
                name="administrativeModePassword"
                render={({ field }) => (
                  <input
                    id="administrative-mode-super-admin-code-input"
                    name="administrative-mode-super-admin-code"
                    type="text"
                    className="password-input password-gate-obfuscated"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    {...PASSWORD_GATE_INPUT_DATA_ATTRS}
                    onKeyDown={e => {
                      if (e.key === "Enter" && administrativeModePassword) {
                        handleSubmit(onSubmit)();
                      }
                    }}
                    aria-label={t("ui.PASSWORD")}
                  />
                )}
              />
            </div>
          </div>
          <div className="price-mode-section">
            <div className="current-mode-wrapper">
              <div className="current-mode-label-text">
                {t("ui.CURRENT_ACTIVE_MODE")}
              </div>
              <div className={`current-mode-value ${currentModeClassName}`}>
                {currentModeLabel}
              </div>
            </div>
            <div className="set-mode-section">
              <div className="set-mode-label">
                {t("general.SET_PRICE_DISPLAY_MODE_TO")}
              </div>
              <Controller
                control={control}
                name="priceMode"
                render={({ field }) => (
                  <FormControl size="small" className="price-mode-form-control">
                    <InputLabel id="administrative-mode-price-mode-label">
                      {t("general.PRICE_MODE")}
                    </InputLabel>
                    <Select
                      labelId="administrative-mode-price-mode-label"
                      id="administrative-mode-price-mode-select"
                      label={t("general.PRICE_MODE")}
                      {...field}
                    >
                      <MenuItem value={SuperAdminPriceMode.Client}>
                        {t("general.CLIENT_PRICES")}
                      </MenuItem>
                      <MenuItem value={SuperAdminPriceMode.Supplier}>
                        {t("general.SUPPLIER_PRICES")}
                      </MenuItem>
                      <MenuItem value={SuperAdminPriceMode.Both}>
                        {t("general.CLIENT_AND_SUPPLIER_PRICES")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </div>
          </div>
        </AdministrativeModeWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
